/* eslint-disable */
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Button,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, formatStatus, formatPaymentMethod } from 'utils/format';
import { MdVisibility } from 'react-icons/md';

const columnHelper = createColumnHelper();

export default function PaymentsTable(props) {
  const { tableData = [] } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const buttonBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const buttonHover = useColorModeValue(
    { bg: 'secondaryGray.400' },
    { bg: 'whiteAlpha.50' }
  );
  const iconColor = useColorModeValue('brand.500', 'white');

  const columns = [
    columnHelper.accessor('payment_date', {
      id: 'payment_date',
      header: 'Payment Date',
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {formatDate(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: 'Amount',
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatCurrency(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('payment_method', {
      id: 'payment_method',
      header: 'Payment Method',
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {formatPaymentMethod(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('reference_number', {
      id: 'reference_number',
      header: 'Reference Number',
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontFamily="mono">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: (info) => {
        const status = info.getValue();
        const colorScheme = 
          status === 'validated' ? 'green' : 
          status === 'pending' ? 'yellow' : 
          status === 'rejected' ? 'red' : 'gray';
        
        return (
          <Badge
            colorScheme={colorScheme}
            bg={bgStatus}
            fontSize="xs"
            fontWeight="700"
            px="8px"
            py="2px"
          >
            {formatStatus(status)}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('validated_by_name', {
      id: 'validated_by_name',
      header: 'Validated By',
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('validated_at', {
      id: 'validated_at',
      header: 'Validated At',
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() ? formatDate(info.getValue()) : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('notes', {
      id: 'notes',
      header: 'Notes',
      cell: (info) => (
        <Text 
          color={textColor} 
          fontSize="sm" 
          noOfLines={2}
          maxW="200px"
          title={info.getValue() || ''}
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          ACTIONS
        </Text>
      ),
      cell: (info) => (
        <IconButton
          aria-label="View payment"
          icon={<MdVisibility />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          onClick={() => navigate(`/admin/payment/${info.row.original.id}`)}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  return (
    <Card>
      <Box>
        <Table variant="simple" color="secondaryGray.700" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id} 
                    borderColor={borderColor} 
                    pe="10px"
                    cursor="pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Flex
                      justifyContent="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="secondaryGray.600"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py="20px">
                  <Text color={textColor} fontSize="sm">
                    No payments found
                  </Text>
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      fontSize={{ sm: "14px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}

